import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";
import type { AnnotationRange } from "../../types/api";

interface Props {
  docId: string;
  versionId: string;
  quote: string;
  range: AnnotationRange;
  sectionId?: string;
  threadId?: string;
  onClose: () => void;
}

/**
 * Modal/drawer used solely to CREATE a new comment annotation.
 * Viewing existing comments happens in the inline popover (AnnotationsLayer).
 */
export function CommentForm({
  docId,
  versionId,
  quote,
  range,
  sectionId,
  threadId,
  onClose,
}: Props) {
  const [body, setBody] = useState("");
  const qc = useQueryClient();

  const annotations = useQuery({
    queryKey: ["doc-annotations", docId],
    queryFn: () => api.listAnnotations(docId),
  });

  const thread = threadId
    ? (annotations.data?.items.filter(
        (a) => a.kind === "comment" && (a.id === threadId || a.thread_id === threadId),
      ) ?? [])
    : [];

  const create = useMutation({
    mutationFn: async () => {
      return api.createAnnotation(docId, {
        kind: "comment",
        body,
        version_id: versionId,
        range,
        section_id: sectionId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["doc-annotations", docId] });
      setBody("");
      toast.success("Comentario publicado");
      onClose();
    },
    onError: (err) => toast.error(`No se pudo publicar: ${err}`),
  });

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[420px] sm:w-[460px] p-0 flex flex-col">
        <SheetHeader>
          <SheetTitle>Comentar</SheetTitle>
          <SheetDescription>
            Añade un comentario vinculado al fragmento seleccionado.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-3">
          <blockquote className="border-l-2 border-(--accent-300) pl-3 text-xs italic text-(--text-secondary)">
            "{quote.length > 280 ? `${quote.slice(0, 280)}…` : quote}"
          </blockquote>
        </div>

        {thread.length > 0 && (
          <div className="flex-1 overflow-y-auto border-t border-(--border-subtle) px-4 py-3">
            <h4 className="mb-2 text-xs font-semibold uppercase text-(--text-tertiary)">Hilo</h4>
            <ul className="space-y-3">
              {thread.map((c) => (
                <li key={c.id} className="text-sm">
                  <div className="flex items-baseline gap-2 text-xs text-(--text-tertiary)">
                    <strong className="text-(--text-primary)">{c.author_subject}</strong>
                    <span>{formatRelative(c.created_at)}</span>
                  </div>
                  <p className="mt-0.5">{c.body}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-auto border-t border-(--border-subtle) p-3">
          <div className="flex gap-2">
            <Input
              placeholder="Tu comentario… @mencionar"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (body.trim()) create.mutate();
                }
              }}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={!body.trim() || create.isPending}
              onClick={() => create.mutate()}
              aria-label="Publicar"
            >
              <Send size={13} />
            </Button>
          </div>
          <p className="mt-1 text-[10px] text-(--text-tertiary)">⌘↵ enviar</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
